import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Waves, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { OceanBackground } from "@/components/AppShell";
import { LanguageToggle } from "@/components/LanguageToggle";
import { GOALS, kebab } from "@/lib/constants";
import { cn } from "@/lib/utils";

export default function Onboarding() {
  const navigate = useNavigate();
  const { createProfile } = useProfile();
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [goal, setGoal] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleStart = async () => {
    if (!goal) {
      toast.error(t("onboarding.chooseGoalError"));
      return;
    }
    setSaving(true);
    try {
      await createProfile({ name: name.trim(), goal });
      navigate("/", { replace: true });
    } catch {
      toast.error(t("onboarding.createError"));
      setSaving(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <OceanBackground variant="sheen" />
      <div className="relative z-10 max-w-md mx-auto px-5 pt-[max(3rem,env(safe-area-inset-top))] pb-10 flex flex-col min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-cyan-300/15 border border-cyan-300/30 flex items-center justify-center">
                <Waves className="text-cyan-300" size={24} />
              </div>
              <span className="text-xs uppercase tracking-[0.28em] text-cyan-300/80 font-medium">
                {t("onboarding.stage")}
              </span>
            </div>
            <LanguageToggle />
          </div>

          {/* Brand name is never translated */}
          <h1 className="mt-6 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            Blue Ocean
            <br />
            Motion
          </h1>
          <p className="mt-3 text-lg text-cyan-100/90 font-medium">
            {t("onboarding.tagline")}
          </p>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            {t("onboarding.intro")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8"
        >
          <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
            {t("onboarding.nameLabel")}{" "}
            <span className="normal-case tracking-normal">{t("onboarding.nameOptional")}</span>
          </label>
          <Input
            data-testid="onboarding-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("onboarding.namePlaceholder")}
            maxLength={40}
            className="mt-2 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-cyan-300/60"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 flex-1"
        >
          <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-3">
            {t("onboarding.goalQuestion")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {GOALS.map((g) => (
              <button
                key={g}
                type="button"
                data-testid={`onboarding-goal-option-${kebab(g)}`}
                onClick={() => setGoal(g)}
                className={cn(
                  "rounded-2xl border p-4 text-sm font-medium text-left transition-colors duration-200 active:scale-[0.98]",
                  goal === g
                    ? "bg-cyan-300/15 border-cyan-300/40 text-cyan-100 shadow-[0_0_30px_rgba(34,211,238,0.10)]"
                    : "bg-white/5 border-white/10 text-white/85 hover:bg-white/8"
                )}
              >
                {t(`goals.${g}`)}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 sticky bottom-0 pb-[env(safe-area-inset-bottom)]"
        >
          <Button
            data-testid="onboarding-start-button"
            onClick={handleStart}
            disabled={saving || !goal}
            className="w-full h-13 py-6 rounded-xl bg-cyan-300 text-slate-950 text-base font-semibold hover:bg-cyan-200 active:scale-[0.98] shadow-[0_0_0_1px_rgba(34,211,238,0.25),0_12px_40px_rgba(34,211,238,0.18)] disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              <>
                {t("onboarding.start")} <ArrowRight size={18} className="ml-1" />
              </>
            )}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
